import shallowEqual from 'react-utils/shallowEqual';
import { arrangeNodePositionSubsetById, getBoundsFuncForConfig, getElementsFuncForConfig, getTreeRootElementFuncForConfig
// @ts-ignore no types
} from './nodePositionUtils';
// @ts-ignore no types
import { getComputedMargin } from '../dom/margin';
import { isSectionNode, isModuleNode } from 'layout-data-lib/GridLayoutDataTree/utils';
import { layoutNodeTypes } from 'layout-dnd-utils/constants/dndAreaHoverPositionConstants';
import { isUnifiedGap } from 'layout-data-lib/GridLayoutDataTree/typenameUtils';
import { getPegboardContainerWidth, getPegboardRowSize } from '../geometry/pegboard';
const getGridLayoutTreePosition = (id, bounds, margin, breakpoint) => {
  return Object.assign({}, bounds, {
    type: layoutNodeTypes.GRID_LAYOUT_ROOT,
    id,
    depth: 0,
    isGridCell: false,
    margin: {
      top: margin.top,
      left: margin.left
    },
    gridLayoutId: undefined,
    breakpoint
  });
};
const DEFAULT_PEGBOARD_GAP_SIZE = 11;
const getGridGapSizes = (gridNode, breakpoint, scale) => {
  const {
    gap: gapDefinition
  } = gridNode.getBreakpointStyles(breakpoint);
  if (!gapDefinition) {
    return {
      rowGap: DEFAULT_PEGBOARD_GAP_SIZE * scale,
      columnGap: DEFAULT_PEGBOARD_GAP_SIZE * scale
    };
  }
  if (isUnifiedGap(gapDefinition)) {
    return {
      rowGap: gapDefinition.size.value * scale,
      columnGap: gapDefinition.size.value * scale
    };
  }
  return {
    rowGap: gapDefinition.rowGap.value * scale,
    columnGap: gapDefinition.columnGap.value * scale
  };
};
export const getGridTemplateTrackSize = ({
  gridNode,
  pegboardWidth,
  breakpoint,
  scale,
  viewportWidthBreakpointMap
}) => {
  const {
    columns
  } = gridNode.getContainerLayout(breakpoint);
  const {
    rowGap,
    columnGap
  } = getGridGapSizes(gridNode, breakpoint, scale);
  const parentNode = gridNode.getParentNode();
  if (!isSectionNode(parentNode)) {
    throw new Error('Pegboard parent is not section node');
  }
  const trackSizes = {
    rowSize: 0,
    columnSize: 0,
    rowGap,
    columnGap
  };
  const styles = parentNode.getBreakpointStyles(breakpoint);
  const pegboardContainerWidth = getPegboardContainerWidth(viewportWidthBreakpointMap[breakpoint], styles.contentWidth, styles.margin, styles.padding);
  trackSizes.rowSize = getPegboardRowSize(breakpoint, pegboardContainerWidth, scale);
  if (columns.length > 0 && pegboardWidth > 0) {
    const totalColumnGap = trackSizes.columnGap * (columns.length - 1);
    trackSizes.columnSize = (pegboardWidth - totalColumnGap) / columns.length;
  }
  return trackSizes;
};
const gridNodeExtractorBuilder = (nodePositions, previousNodePositionsForAreaById, {
  breakpoint,
  breakpointsByInheritance,
  getBoundsForConfig,
  getElementsForConfig,
  gridLayoutRootElement,
  scale,
  viewportWidthBreakpointMap,
  onlyMeasureNodeIds
}) => {
  const nodePositionLookupMap = {};
  const gridNodeExtractor = ({
    gridNode,
    depth
  }) => {
    const gridNodeId = gridNode.getId();
    const prevPosition = previousNodePositionsForAreaById[gridNodeId];
    if (prevPosition && onlyMeasureNodeIds && onlyMeasureNodeIds.length > 0 && !onlyMeasureNodeIds.includes(gridNodeId)) {
      nodePositions.push(prevPosition);
      return {
        id: gridNodeId,
        nodeElement: null
      };
    }
    const gridLayoutId = gridNode.getRootId();
    const gridNodeElement = getElementsForConfig(gridNode, gridLayoutRootElement);
    const bounds = getBoundsForConfig(gridNodeElement[0]);
    if (!bounds.hasBounds) {
      return {
        id: gridNodeId,
        nodeElement: gridNodeElement[0]
      };
    }
    const parentIdForNode = gridNode.getParentNodeId();
    const parentNodePosition = nodePositionLookupMap[parentIdForNode];
    let newNodePosition;
    if (isSectionNode(gridNode)) {
      newNodePosition = Object.assign({}, bounds, {
        depth: 1,
        gridLayoutId: gridNode.getRootId(),
        id: gridNodeId,
        hasLayoutStyles: gridNode.hasStyles(),
        numChildren: gridNode.hasChildNodeIds() ? gridNode.getChildNodeIds().length : 0,
        parentId: gridNode.getRootId(),
        type: layoutNodeTypes.GRID_LAYOUT_SECTION,
        isGridCell: false,
        breakpoint
      });
    } else if (gridNode.isStandardContainer()) {
      let gridLayoutContainerType;
      if (gridNode.isHorizontalStack(breakpoint)) {
        gridLayoutContainerType = layoutNodeTypes.GRID_LAYOUT_HORIZONTAL_STACK;
      } else if (gridNode.isVerticalStack(breakpoint)) {
        gridLayoutContainerType = layoutNodeTypes.GRID_LAYOUT_VERTICAL_STACK;
      } else {
        gridLayoutContainerType = layoutNodeTypes.GRID_LAYOUT_GRID;
      }
      newNodePosition = Object.assign({}, bounds, {
        depth,
        gridLayoutId,
        id: gridNodeId,
        hasLayoutStyles: gridNode.hasStyles(),
        numChildren: gridNode.hasChildNodeIds() ? gridNode.getChildNodeIds().length : 0,
        parentId: parentIdForNode,
        type: gridLayoutContainerType,
        isGridCell: (parentNodePosition === null || parentNodePosition === void 0 ? void 0 : parentNodePosition.type) === layoutNodeTypes.GRID_LAYOUT_GRID,
        breakpoint
      });
    } else if (gridNode.isPegboardContainer()) {
      const layout = gridNode.getContainerLayout(breakpoint);
      newNodePosition = Object.assign({}, bounds, getGridTemplateTrackSize({
        gridNode,
        pegboardWidth: bounds.width,
        breakpoint,
        scale,
        viewportWidthBreakpointMap
      }), {
        depth,
        gridLayoutId,
        id: gridNodeId,
        hasLayoutStyles: gridNode.hasStyles(),
        numChildren: gridNode.hasChildNodeIds() ? gridNode.getChildNodeIds().length : 0,
        parentId: parentIdForNode,
        type: layoutNodeTypes.PEGBOARD,
        isGridCell: false,
        rows: layout.rows.length,
        columns: layout.columns.length,
        breakpoint
      });
    } else if (isModuleNode(gridNode)) {
      newNodePosition = Object.assign({}, bounds, {
        depth,
        gridLayoutId,
        // TODO: isFixedPosition and isAbsolutePosition only get set when
        // computing bounds with applyPositionInfo. Consider toggling that on
        // after validating against perf concerns with getComputedStyles. We
        // also may need to split up the method used for bounds calculation
        // between modules and other grid content types.
        isFixedPosition: false,
        isAbsolutePosition: false,
        id: gridNodeId,
        hasLayoutStyles: gridNode.hasStyles(),
        parentId: gridNode.getParentNodeId(),
        type: layoutNodeTypes.GRID_LAYOUT_MODULE,
        inPegboard: false,
        isGridCell: (parentNodePosition === null || parentNodePosition === void 0 ? void 0 : parentNodePosition.type) === layoutNodeTypes.GRID_LAYOUT_GRID,
        breakpoint
      });
      const {
        gridSpanning
      } = gridNode.getEditorProvidedBreakpointStyles(breakpoint);
      const effectiveZIndex = gridNode.getZIndex(breakpoint, breakpointsByInheritance);
      if ((parentNodePosition === null || parentNodePosition === void 0 ? void 0 : parentNodePosition.type) === layoutNodeTypes.PEGBOARD && gridSpanning) {
        newNodePosition.rowsSpanned = gridSpanning.rowsSpanned;
        newNodePosition.columnsSpanned = gridSpanning.columnsSpanned;
        newNodePosition.inPegboard = true;
      }
      if (effectiveZIndex != null) {
        newNodePosition.zIndex = effectiveZIndex;
      }
    } else {
      throw new Error('Attempting to get the node position of an invalid grid content type');
    }
    if (prevPosition && prevPosition.id === gridNodeId && shallowEqual(prevPosition, newNodePosition)) {
      nodePositionLookupMap[prevPosition.id] = prevPosition;
      nodePositions.push(prevPosition);
    } else if (newNodePosition) {
      nodePositionLookupMap[newNodePosition.id] = newNodePosition;
      nodePositions.push(newNodePosition);
    }
    return {
      id: gridNodeId,
      nodeElement: gridNodeElement[0]
    };
  };
  return gridNodeExtractor;
};
const calculateGridLayoutTreeNodePositions = (gridLayoutIds, gridLayoutIteratorMap, previousNodePositions = {}, subConfig = {}, breakpoint = 'default', scale = 1, viewportWidthBreakpointMap, onlyMeasureNodeIds, breakpointsByInheritance = {}) => {
  const newNodePositions = {};
  if (subConfig.startCallback) {
    subConfig.startCallback(subConfig);
  }
  const getBoundsForConfig = getBoundsFuncForConfig(subConfig);
  const getElementsForConfig = getElementsFuncForConfig(subConfig);
  const previousNodePositionsById = arrangeNodePositionSubsetById(previousNodePositions, subConfig);
  const getTreeRootForConfig = getTreeRootElementFuncForConfig(subConfig);
  gridLayoutIds.forEach(id => {
    const gridLayoutRootElement = getTreeRootForConfig(id);
    const newBounds = getBoundsForConfig(gridLayoutRootElement);
    const newMargin = getComputedMargin(gridLayoutRootElement);
    const layoutTreePosition = getGridLayoutTreePosition(id, newBounds, newMargin, breakpoint);
    const previousNodePositionsForArea = previousNodePositions[id] || [];
    const previousRootPosition = previousNodePositionsForArea[0];
    const previousNodePositionsForAreaById = previousNodePositionsById[id] || {};
    const newNodePositionsForArea = [];

    // Manually do a shallow equal check on the nested margin property (that is the only nested
    // thing on a root position that wouldn't be correctly checked by a single `shallowEqual` call)
    if (previousRootPosition && previousRootPosition.type === layoutNodeTypes.GRID_LAYOUT_ROOT && shallowEqual(previousRootPosition.margin, layoutTreePosition.margin)) {
      layoutTreePosition.margin = previousRootPosition.margin;
    }
    if (previousRootPosition && previousRootPosition.id === id && shallowEqual(previousRootPosition, layoutTreePosition)) {
      newNodePositionsForArea.push(previousRootPosition);
    } else {
      newNodePositionsForArea.push(layoutTreePosition);
    }
    if (gridLayoutIteratorMap) {
      gridLayoutIteratorMap[id]({
        gridNodeCallback: gridNodeExtractorBuilder(newNodePositionsForArea, previousNodePositionsForAreaById, {
          getBoundsForConfig,
          getElementsForConfig,
          gridLayoutRootElement,
          breakpoint,
          breakpointsByInheritance,
          viewportWidthBreakpointMap,
          scale,
          onlyMeasureNodeIds
        })
      });
    }

    // Compare if the node position stack for this specific layout section is the same has before.
    // And if it is return the previous position array reference (this works because `rowExtractorBuilder`
    // and `columnExtractorBuilder` are already returning the same position reference for every item in
    // the array if it hasn't changed)
    if (shallowEqual(newNodePositionsForArea, previousNodePositionsForArea)) {
      newNodePositions[id] = previousNodePositionsForArea;
    } else {
      newNodePositions[id] = newNodePositionsForArea;
    }
  });
  if (subConfig.endCallback) {
    subConfig.endCallback(subConfig);
  }

  // If _all_ the node positions for every layout section are the same, return the previous reference
  if (shallowEqual(newNodePositions, previousNodePositions)) {
    return previousNodePositions;
  }
  return newNodePositions;
};
export default calculateGridLayoutTreeNodePositions;